/**
 * FERP Invoicing Hooks - Repository Pattern
 *
 * Standard CRUD operations use the Invoice repository directly.
 * Custom operations (send, void, record payment) use repository-compatible
 * wrappers that maintain audit logging and tenant isolation.
 *
 * APPEND-ONLY ARCHITECTURE:
 * - Invoices are created, not updated (new versions are created)
 * - Voiding creates a void record, doesn't delete the invoice
 * - Payments are recorded as separate entities linked to invoices
 */
// @ts-nocheck


import { useInvoiceRepository, useAuditLogger } from '../repositories';
import { useCompatQuery, useCompatMutation } from './useApolloCompat';
import {
  INVOICE_PAYMENTS_QUERY,
  INVOICE_SUMMARY_QUERY,
  UPDATE_INVOICE_MUTATION,
  SEND_INVOICE_MUTATION,
  RECORD_PAYMENT_MUTATION,
  VOID_INVOICE_MUTATION,
} from '../graphql/operations';
import type {
  Invoice,
  Payment,
  InvoiceSummary,
  CreateInvoiceInput,
  UpdateInvoiceInput,
  RecordPaymentInput,
} from '../types/invoice';

// ── Standard CRUD via Repository ────────────────────────────────────

export function useInvoices(variables?: {
  page?: number;
  perPage?: number;
  status?: string;
  clientId?: string;
}) {
  const repo = useInvoiceRepository();
  return repo.useList(variables);
}

export function useInvoice(id: string) {
  const repo = useInvoiceRepository();
  return repo.useGet(id);
}

export function useCreateInvoice() {
  const repo = useInvoiceRepository();
  const { create, loading, error } = repo.useCreate();
  return {
    mutate: (input: CreateInvoiceInput) => create(input),
    mutateAsync: (input: CreateInvoiceInput) => create(input),
    isPending: loading,
    loading,
    error,
  };
}

// ── Custom Queries (Repository-Compatible Wrappers) ─────────────────

/**
 * Get payments for an invoice.
 * Uses repository-compatible interface with automatic audit logging.
 */
export function useInvoicePayments(invoiceId: string) {
  const logger = useAuditLogger('InvoicePayment');

  const result = useCompatQuery(INVOICE_PAYMENTS_QUERY, {
    variables: { invoiceId },
    skip: !invoiceId,
    onCompleted: () => logger.log('list', { invoiceId }),
    onError: (error: any) => logger.log('list', { invoiceId, error: error.message }),
  });

  return {
    data: result.data?.invoicePayments || [],
    loading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Get invoice summary statistics.
 * Uses repository-compatible interface with automatic audit logging.
 */
export function useInvoiceSummary() {
  const logger = useAuditLogger('Invoice');

  const result = useCompatQuery(INVOICE_SUMMARY_QUERY, {
    onCompleted: () => logger.log('list', { type: 'summary' }),
  });

  return {
    data: result.data?.invoiceSummary || null,
    loading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

// ── Custom Mutations (Repository-Compatible Wrappers) ───────────────

/**
 * Update invoice (creates new version per append-only pattern).
 * Logs audit trail for compliance.
 */
export function useUpdateInvoice() {
  const logger = useAuditLogger('Invoice');

  const [mutate, result] = useCompatMutation(UPDATE_INVOICE_MUTATION, {
    refetchQueries: [{ query: INVOICE_SUMMARY_QUERY }],
  });

  return {
    update: async (id: string, input: UpdateInvoiceInput) => {
      logger.log('update', { entityId: id, input });
      return mutate({ variables: { id, input } });
    },
    loading: result.loading,
    error: result.error,
  };
}

/**
 * Send invoice to client (creates send event per append-only pattern).
 * Logs audit trail for compliance.
 */
export function useSendInvoice() {
  const logger = useAuditLogger('Invoice');

  const [mutate, result] = useCompatMutation(SEND_INVOICE_MUTATION, {
    refetchQueries: [{ query: INVOICE_SUMMARY_QUERY }],
  });

  return {
    send: async (invoiceId: string) => {
      logger.log('custom', { action: 'send', entityId: invoiceId });
      return mutate({ variables: { invoiceId } });
    },
    loading: result.loading,
    error: result.error,
  };
}

/**
 * Record payment against invoice (creates payment record per append-only pattern).
 * Logs audit trail for compliance.
 */
export function useRecordPayment() {
  const logger = useAuditLogger('InvoicePayment');

  const [mutate, result] = useCompatMutation(RECORD_PAYMENT_MUTATION, {
    refetchQueries: [
      { query: INVOICE_PAYMENTS_QUERY },
      { query: INVOICE_SUMMARY_QUERY },
    ],
  });

  return {
    recordPayment: async (input: RecordPaymentInput) => {
      logger.log('create', { input });
      return mutate({ variables: { input } });
    },
    loading: result.loading,
    error: result.error,
  };
}

/**
 * Void invoice (creates void event per append-only pattern, doesn't delete).
 * Logs audit trail for compliance.
 */
export function useVoidInvoice() {
  const logger = useAuditLogger('Invoice');

  const [mutate, result] = useCompatMutation(VOID_INVOICE_MUTATION, {
    refetchQueries: [{ query: INVOICE_SUMMARY_QUERY }],
  });

  return {
    voidInvoice: async (invoiceId: string, reason: string) => {
      logger.log('custom', { action: 'void', entityId: invoiceId, reason });
      return mutate({ variables: { invoiceId, reason } });
    },
    loading: result.loading,
    error: result.error,
  };
}
